import { useState, useEffect } from 'react';
import axios from 'axios';
import { PropertyCard } from '@/components/PropertyCard';
import { motion } from 'framer-motion';
import { Loader2, Search, SlidersHorizontal, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Locations = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  
  const [filters, setFilters] = useState({
    search: '',
    area: 'all',
    room_type: 'all',
    min_price: '',
    max_price: '',
    sort_by: 'name'
  });

  useEffect(() => {
    fetchProperties();
  }, [page, filters]);

  const fetchProperties = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('per_page', 12);
      
      if (filters.search) params.append('search', filters.search);
      if (filters.area && filters.area !== 'all') params.append('area', filters.area);
      if (filters.room_type && filters.room_type !== 'all') params.append('room_type', filters.room_type);
      if (filters.min_price) params.append('min_price', filters.min_price);
      if (filters.max_price) params.append('max_price', filters.max_price);
      if (filters.sort_by) params.append('sort_by', filters.sort_by);

      const response = await axios.get(`${BACKEND_URL}/api/properties?${params}`);
      setProperties(response.data.properties);
      setTotalPages(response.data.total_pages);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchProperties();
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      area: 'all',
      room_type: 'all',
      min_price: '',
      max_price: '',
      sort_by: 'name'
    });
    setPage(1);
  };

  const hasActiveFilters = filters.search || (filters.area && filters.area !== 'all') || (filters.room_type && filters.room_type !== 'all') || filters.min_price || filters.max_price;

  return (
    <div className="pt-16">
      <section className="bg-gradient-to-br from-white via-purple-50/30 to-white py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h1 className="font-heading text-5xl md:text-7xl font-bold tracking-tight text-gray-900 mb-6">
              Our <span className="text-brand-purple">Locations</span>
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-gray-600 font-body max-w-3xl mx-auto">
              Find your perfect stay across {total} prime locations
            </p>
          </motion.div>

          {/* Search and Filters */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-8">
            <form onSubmit={handleSearch} className="space-y-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <Input
                    type="text"
                    placeholder="Search by property name, location..."
                    value={filters.search}
                    onChange={(e) => setFilters({...filters, search: e.target.value})}
                    className="pl-10 font-body"
                    data-testid="search-input"
                  />
                </div>
                <Button
                  type="button"
                  onClick={() => setShowFilters(!showFilters)}
                  variant="outline"
                  className="font-body"
                  data-testid="toggle-filters-button"
                >
                  <SlidersHorizontal size={18} className="mr-2" />
                  Filters
                </Button>
                <Button
                  type="submit"
                  className="bg-brand-purple text-white hover:bg-brand-purple/90 font-body"
                  data-testid="search-button"
                >
                  Search
                </Button>
              </div>

              {showFilters && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 pt-4 border-t"
                  data-testid="filters-panel"
                >
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block font-body">Area</label>
                    <Select value={filters.area || undefined} onValueChange={(value) => setFilters({...filters, area: value})}>
                      <SelectTrigger className="font-body" data-testid="filter-area">
                        <SelectValue placeholder="All Areas" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Areas</SelectItem>
                        <SelectItem value="Mumbai">Mumbai</SelectItem>
                        <SelectItem value="Pune">Pune</SelectItem>
                        <SelectItem value="Bangalore">Bangalore</SelectItem>
                        <SelectItem value="Delhi">Delhi</SelectItem>
                        <SelectItem value="Hyderabad">Hyderabad</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block font-body">Room Type</label>
                    <Select value={filters.room_type || undefined} onValueChange={(value) => setFilters({...filters, room_type: value})}>
                      <SelectTrigger className="font-body" data-testid="filter-room-type">
                        <SelectValue placeholder="All Types" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="Single">Single Sharing</SelectItem>
                        <SelectItem value="Double">Double Sharing</SelectItem>
                        <SelectItem value="Triple">Triple Sharing</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block font-body">Min Price</label>
                    <Input
                      type="number"
                      placeholder="₹5,000"
                      value={filters.min_price}
                      onChange={(e) => setFilters({...filters, min_price: e.target.value})}
                      className="font-body"
                      data-testid="filter-min-price"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block font-body">Max Price</label>
                    <Input
                      type="number"
                      placeholder="₹20,000"
                      value={filters.max_price}
                      onChange={(e) => setFilters({...filters, max_price: e.target.value})}
                      className="font-body"
                      data-testid="filter-max-price"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block font-body">Sort By</label>
                    <Select value={filters.sort_by} onValueChange={(value) => setFilters({...filters, sort_by: value})}>
                      <SelectTrigger className="font-body" data-testid="filter-sort">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="name">Name</SelectItem>
                        <SelectItem value="price_low">Price: Low to High</SelectItem>
                        <SelectItem value="price_high">Price: High to Low</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </motion.div>
              )}
            </form>

            {hasActiveFilters && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <p className="text-sm text-gray-600 font-body">
                  {total} properties found
                </p>
                <Button
                  onClick={clearFilters}
                  variant="ghost"
                  size="sm"
                  className="text-brand-purple hover:text-brand-purple/80 font-body"
                  data-testid="clear-filters-button"
                >
                  <X size={16} className="mr-1" />
                  Clear Filters
                </Button>
              </div>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-20" data-testid="locations-loading">
              <Loader2 className="animate-spin text-brand-purple" size={48} />
            </div>
          ) : properties.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-2xl" data-testid="locations-empty">
              <p className="text-gray-600 font-body text-lg mb-4">
                No properties found matching your criteria.
              </p>
              <Button
                onClick={clearFilters}
                className="bg-brand-purple text-white hover:bg-brand-purple/90 font-body"
              >
                Clear Filters
              </Button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12" data-testid="locations-grid">
                {properties.map((property, index) => (
                  <motion.div
                    key={property.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <PropertyCard property={property} />
                  </motion.div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2" data-testid="pagination">
                  <Button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    variant="outline"
                    className="font-body"
                    data-testid="prev-page-button"
                  >
                    Previous
                  </Button>
                  
                  <div className="flex gap-2">
                    {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (page <= 3) {
                        pageNum = i + 1;
                      } else if (page >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = page - 2 + i;
                      }
                      
                      return (
                        <Button
                          key={pageNum}
                          onClick={() => setPage(pageNum)}
                          variant={page === pageNum ? "default" : "outline"}
                          className={`font-body ${page === pageNum ? 'bg-brand-purple text-white' : ''}`}
                          data-testid={`page-button-${pageNum}`}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </div>

                  <Button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    variant="outline"
                    className="font-body"
                    data-testid="next-page-button"
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
};
